import React, { useRef, useState } from "react"
import { Grid, Button, CircularProgress } from "@material-ui/core"

import { ChatMessageBuilder } from "@/components"
import { ChatMessageBuilderHandler, Message } from "@/components/ChatMessageBuilder"
import { BuildedMessage } from "@/protocols/messages"
import { letalkLinks } from "@/utils/link"
import { formatChatMessageBuilderVariables } from "@/utils/formatChatMessageBuilderVariables"
import { Variable } from "@/components/ChatMessageBuilder/VariableInput"
import useDidMount from "@/hooks/useDidMount"
import ApiService from "@/services/Api"
import { ErrorType } from "@/hooks/useValidation"
import ErrorHandler from "@/services/ErrorHandler"
import WABATemplateSelector from "@/pages/Admin/MessageBlast/Management/Messages/WABATemplateSelector"
import { WABAChannelMessageTemplate } from "@/@integrations/WABA/protocols/wabaChannelMessageTemplate"
import { MessageBlastMessageType } from "@/protocols/messageBlast"
import { useGlobalStateStore } from "@/store/GlobalState"
import MessageBlastGroupPermissionGate from "@/components/PermissionGate/MessageBlastGroupPermissionGate"

export type MessageBlastContent = {
	messages?: BuildedMessage[] | Message[]
	wabaChannelMessageTemplateId?: number
}

type MessagesProps = {
	isMessageBlastSent: boolean
	messageBlastMessageType: MessageBlastMessageType
	messages: BuildedMessage[]
	templates?: WABAChannelMessageTemplate[]
	wabaChannelMessageTemplateId?: number
	onSave: (content: MessageBlastContent) => void | Promise<void>
}

const Messages: React.FC<MessagesProps> = ({
	isMessageBlastSent,
	messageBlastMessageType,
	messages,
	templates,
	wabaChannelMessageTemplateId,
	onSave
}) => {
	const [savingMessages, setSavingMessages] = useState(false)
	const [messageBlastVariables, setMessageBlastVariables] = useState<Variable[]>([])
	const [selectedWABATemplateId, setSelectedWABATemplateId] = useState<number | undefined>(wabaChannelMessageTemplateId)
	const chatMessageBuilderRef = useRef<ChatMessageBuilderHandler>(null)
	const globalStateStore = useGlobalStateStore()

	const handleSave = async () => {
		setSavingMessages(true)
		chatMessageBuilderRef.current?.blur()

		const messageTypeToSaveHandler: Record<MessageBlastMessageType, () => Promise<void>> = {
			"waba-channel-message-template": async (): Promise<void> => {
				await onSave({ wabaChannelMessageTemplateId: selectedWABATemplateId })
			},
			whatsapp: async (): Promise<void> => {
				const updatedMessages = chatMessageBuilderRef.current?.getMessages() || messages
				await onSave({ messages: updatedMessages })
			}
		}

		const saveHandler = messageTypeToSaveHandler[messageBlastMessageType]
		await saveHandler()

		setSavingMessages(false)
	}

	const isSaveDisabled = () => {
		const messageTypeToDisabled: Record<MessageBlastMessageType, boolean> = {
			"waba-channel-message-template": selectedWABATemplateId === wabaChannelMessageTemplateId,
			whatsapp: false
		}
		return savingMessages || messageTypeToDisabled[messageBlastMessageType]
	}

	const handleGetAvailableVariables = async () => {
		try {
			const response = await ApiService.get("/message-blasts/variables", {
				params: {
					instanceId: globalStateStore.instance.instance_id
				}
			})

			const variables = formatChatMessageBuilderVariables(response.data)
			setMessageBlastVariables(variables)
		} catch (error) {
			ErrorHandler.handle(error as ErrorType)
		}
	}

	const messageTypeToMessageComponent: Record<MessageBlastMessageType, React.ReactNode> = {
		"waba-channel-message-template": (
			<WABATemplateSelector
				selectedWABATemplateId={selectedWABATemplateId}
				templates={templates}
				disabled={isMessageBlastSent}
				onChange={(template) => setSelectedWABATemplateId(template)}
			/>
		),
		whatsapp: (
			<ChatMessageBuilder
				renderWithInitialMessage
				disabledInputs={["mentions", "custom-link", "ai-equivalent-variation"]}
				disabled={isMessageBlastSent}
				disabledMessage="Você não pode editar uma mensagem após o envio."
				ref={chatMessageBuilderRef}
				initialMessages={messages}
				variables={messageBlastVariables}
				helpLink={{
					from: "message-blast",
					href: letalkLinks.wikiHowToCustomizeMessageBatch
				}}
			/>
		)
	}

	useDidMount(() => {
		handleGetAvailableVariables()
	})

	return (
		<Grid container justifyContent="flex-end" spacing={2}>
			<Grid item xs={12}>
				<MessageBlastGroupPermissionGate action="update">
					{messageTypeToMessageComponent[messageBlastMessageType]}
				</MessageBlastGroupPermissionGate>
			</Grid>

			{!isMessageBlastSent && (
				<Grid item>
					<Button
						variant="contained"
						color="primary"
						onClick={handleSave}
						endIcon={savingMessages && <CircularProgress size={20} />}
						disabled={isSaveDisabled()}
					>
						SALVAR
					</Button>
				</Grid>
			)}
		</Grid>
	)
}

export default Messages
